// ConciergeBubble - Message bubble component

import React from 'react';
import { Sparkles } from 'lucide-react';
import type { ConciergeMessage } from '../../types/concierge';
import type { Place } from '../../types';
import { ConciergePlaceCard } from './ConciergePlaceCard';

interface ConciergeBubbleProps {
  message: ConciergeMessage;
  onPlacePreview: (place: Place) => void;
  places: Place[];
}

export function ConciergeBubble({ message, onPlacePreview, places }: ConciergeBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
      )}

      <div className={`max-w-[90%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Message bubble */}
        <div
          className={`px-4 py-3 rounded-2xl ${
            isUser
              ? 'bg-indigo-500 text-white rounded-br-md'
              : 'bg-white text-gray-900 rounded-bl-md shadow-sm border border-gray-100'
          }`}
        >
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        </div>

        {/* Place recommendations */}
        {message.placeRecommendations && message.placeRecommendations.length > 0 && (
          <div className="mt-3 space-y-3 w-full">
            {message.placeRecommendations.map((rec) => {
              const place = places.find((p) => p.id === rec.placeId);
              if (!place) return null;

              return (
                <ConciergePlaceCard
                  key={rec.placeId}
                  place={place}
                  reason={rec.reason}
                  onViewDetails={() => onPlacePreview(place)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConciergeBubble;
